import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { Artefact } from "@/types/artefacts";
import { ARTEFACT_CONFIG } from "@/config/artefacts";
import { escapeHtml } from "@/utils/htmlEscape";
import { logger } from "@/telemetry";

interface HTMLArtefactViewerProps {
  artefact: Artefact;
}

/**
 * Content-Security-Policy meta tag injected into all iframe HTML.
 * - connect-src 'none': blocks fetch/XHR/WebSocket (prevents data exfiltration)
 * - script-src: allows inline scripts + CDN libraries (Chart.js, D3, Mermaid)
 * - img-src: allows data URIs, blobs, and HTTPS images
 *
 * @see PWI-194 — REM-05: Lack of Input and Output Encoding
 */
const CSP_META = `<meta http-equiv="Content-Security-Policy" content="default-src 'unsafe-inline' 'unsafe-eval' data: blob:; script-src 'unsafe-inline' 'unsafe-eval' https://cdn.jsdelivr.net https://cdnjs.cloudflare.com; style-src 'unsafe-inline' https://fonts.googleapis.com https://cdn.jsdelivr.net; font-src data: https://fonts.gstatic.com https://cdn.jsdelivr.net; img-src data: blob: https:; connect-src 'none';">`;

/**
 * Script injected into the iframe for dark mode and height communication.
 * Uses postMessage instead of window.parent.document access (which would
 * require allow-same-origin and enable XSS attacks).
 *
 * NOTE: No event.origin validation in the listener because the iframe has
 * sandbox="allow-scripts" without allow-same-origin, giving it an opaque
 * origin ("null"). Origin-based validation is impossible. The worst an
 * attacker can do by sending a forged ARTEFACT_THEME is toggle dark mode
 * styling — no security impact. Height messages use '*' as targetOrigin
 * for the same reason (opaque origin cannot specify a concrete target).
 */
const IFRAME_SCRIPT = `<script>
  // Listen for theme info from parent (origin validation omitted — see JSDoc above)
  window.addEventListener('message', function(event) {
    if (event.data && event.data.type === 'ARTEFACT_THEME') {
      var existingStyle = document.getElementById('artefact-dark-mode');
      if (event.data.isDark) {
        document.documentElement.classList.add('dark');
        if (!existingStyle) {
          var style = document.createElement('style');
          style.id = 'artefact-dark-mode';
          style.textContent =
            'body { background-color: transparent !important; color: #e4e4e7 !important; }' +
            'table { background-color: transparent !important; }' +
            'th { background-color: #27272a !important; color: #e4e4e7 !important; }' +
            'td { background-color: #18181b !important; color: #e4e4e7 !important; }' +
            'td, th { border-color: #3f3f46 !important; }';
          document.head.appendChild(style);
        }
      } else {
        document.documentElement.classList.remove('dark');
        if (existingStyle) existingStyle.remove();
      }
    }
  });

  // Report height to parent via postMessage
  function reportHeight() {
    try {
      var height = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'ARTEFACT_RESIZE', height: height }, '*');
    } catch (e) {
      console.warn('[ArtefactViewer] reportHeight failed:', e);
    }
  }

  window.addEventListener('load', function() {
    reportHeight();
    setTimeout(reportHeight, 500);
    setTimeout(reportHeight, 2000);
  });

  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(reportHeight).observe(document.documentElement);
  }
</script>`;

const DARK_MODE_CSS = `
  @media (prefers-color-scheme: dark) {
    body { background-color: transparent !important; color: #e4e4e7 !important; }
    table { background-color: transparent !important; }
    th { background-color: #27272a !important; color: #e4e4e7 !important; }
    td { background-color: #18181b !important; color: #e4e4e7 !important; }
    td, th { border-color: #3f3f46 !important; }
  }
  .dark body { color: #e4e4e7; }
  .dark table { background-color: transparent; }
  .dark th { background-color: #27272a !important; color: #e4e4e7; }
  .dark td { background-color: #18181b !important; color: #e4e4e7; }
  .dark th, .dark td { border-color: #3f3f46; }
`;

const SECURITY_INJECTION = `${CSP_META}\n<style>${DARK_MODE_CSS}</style>\n${IFRAME_SCRIPT}`;

const createHtmlContent = (content: string, title: string): string => {
  // Check if it's already a complete HTML document
  if (content.includes("<html")) {
    // Inject CSP, dark mode styles, and postMessage script into existing HTML.
    // Try </head> first, fall back to before <body, then prepend as last resort.
    // Without this fallback, malformed HTML (no </head>) would bypass CSP entirely.
    if (/<\/head>/i.test(content)) {
      return content.replace(/<\/head>/i, `${SECURITY_INJECTION}\n</head>`);
    }
    if (/<body/i.test(content)) {
      logger.info("[ArtefactViewer] CSP injected before <body> — HTML missing </head> tag");

      return content.replace(/<body/i, `<head>${SECURITY_INJECTION}</head>\n<body`);
    }
    logger.info("[ArtefactViewer] CSP prepended — HTML missing both </head> and <body> tags");

    return `${SECURITY_INJECTION}\n${content}`;
  }

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${escapeHtml(title)}</title>
        ${CSP_META}
        <style>
          body {
            margin: 0;
            padding: 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: transparent;
            color: #1a1a1a;
          }
          table {
            border-collapse: collapse;
            width: 100%;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #f2f2f2;
          }
          ${DARK_MODE_CSS}
        </style>
        ${IFRAME_SCRIPT}
      </head>
      <body>
        ${content}
      </body>
    </html>
  `;
};

const isChartContent = (content: string, title: string): boolean => {
  return (
    content.includes("canvas") ||
    content.includes("Chart") ||
    content.includes("chart") ||
    content.includes("ctx.") ||
    title.toLowerCase().includes("chart")
  );
};

export const HTMLArtefactViewer: React.FC<HTMLArtefactViewerProps> = ({ artefact }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const htmlContent = useMemo(
    () => createHtmlContent(artefact.content, artefact.title),
    [artefact.content, artefact.title],
  );
  const isChart = useMemo(
    () => isChartContent(artefact.content, artefact.title),
    [artefact.content, artefact.title],
  );

  // Listen for ARTEFACT_RESIZE messages from the iframe to auto-adjust height
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.type === "ARTEFACT_RESIZE" &&
        typeof event.data.height === "number" &&
        isFinite(event.data.height) &&
        iframeRef.current &&
        event.source === iframeRef.current.contentWindow
      ) {
        const height = Math.max(
          ARTEFACT_CONFIG.iframe.defaultHeight,
          Math.min(ARTEFACT_CONFIG.iframe.maxHeight, event.data.height),
        );

        iframeRef.current.style.height = height + "px";
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Send current dark/light theme to the sandboxed iframe via postMessage.
  // Uses '*' as targetOrigin because the iframe has an opaque origin (no allow-same-origin).
  // Only non-sensitive data (isDark boolean) is sent — no security impact.
  const sendThemeMessage = useCallback(() => {
    if (iframeRef.current?.contentWindow?.postMessage) {
      const isDark = document.documentElement.classList.contains("dark");

      iframeRef.current.contentWindow.postMessage({ type: "ARTEFACT_THEME", isDark }, "*");
    }
  }, []);

  // Re-send theme when the user toggles dark mode in the parent app
  useEffect(() => {
    const observer = new MutationObserver(sendThemeMessage);

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, [sendThemeMessage]);

  return (
    <div className="bg-default-100 dark:bg-default-100/50 rounded-lg border border-default-200 dark:border-default-700">
      <div className="text-xs text-default-600 dark:text-default-400 p-2 font-medium border-b border-default-200 dark:border-default-700">
        {isChart ? "Interactive Chart" : "HTML Content"} • {artefact.metadata?.estimatedSize}
      </div>
      <div className="bg-white dark:bg-default-50">
        <iframe
          key={artefact.id}
          ref={iframeRef}
          className="w-full border-0"
          data-artefact-id={artefact.id}
          sandbox="allow-scripts"
          srcDoc={htmlContent}
          style={{ minHeight: `${ARTEFACT_CONFIG.iframe.defaultHeight}px` }}
          onLoad={sendThemeMessage}
        />
      </div>
    </div>
  );
};
